/**
 * Firestore bindings for /users/{uid}/folders/{folderId}.
 *
 * Folders are a tag-like system for organizing fiches in the sidebar and
 * "Mes fiches" page. A fiche can belong to multiple folders at once (see
 * `FicheDoc.folderIds`). Hierarchy is limited to 2 levels (a folder with
 * a `parentId` cannot itself be a parent) — enforced client-side here.
 */

import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  Timestamp,
  updateDoc,
  where,
  writeBatch,
  type Unsubscribe,
} from "firebase/firestore";
import { auth, db } from "./firebase";

// ─── Palette ──────────────────────────────────────────────────────────────
// Keep aligned with tailwind.config.ts. UI (PR #2) maps each token to
// concrete classes; storing the token lets us restyle without migrating data.
export const FOLDER_COLORS = [
  "lime",
  "forest",
  "brick",
  "ink",
  "slate",
  "sky",
] as const;
export type FolderColor = (typeof FOLDER_COLORS)[number];

export type FolderDoc = {
  id: string;
  userId: string;
  name: string;
  color: FolderColor;
  icon: string | null; // free-form emoji ("🏙️", "🏡", …) — null = no icon
  parentId: string | null; // max 1 level of nesting (a child cannot itself be a parent)
  order: number; // manual sort within the same parent scope
  createdAt: Timestamp | null;
  updatedAt: Timestamp | null;
};

// ─── Paths ────────────────────────────────────────────────────────────────
function foldersCol(uid: string) {
  return collection(db, "users", uid, "folders");
}
function folderRef(uid: string, folderId: string) {
  return doc(db, "users", uid, "folders", folderId);
}
function fichesCol(uid: string) {
  return collection(db, "users", uid, "fiches");
}

// ─── Create ───────────────────────────────────────────────────────────────
export type CreateFolderInput = {
  name: string;
  color: FolderColor;
  icon?: string | null;
  parentId?: string | null;
  order?: number;
};

export async function createFolder(input: CreateFolderInput): Promise<FolderDoc> {
  const user = auth.currentUser;
  if (!user) throw new Error("Not signed in");

  const name = input.name.trim();
  if (!name) throw new Error("Folder name required");

  // Enforce 2-level hierarchy: reject if the chosen parent already has a parent.
  if (input.parentId) {
    const parentSnap = await getDoc(folderRef(user.uid, input.parentId));
    if (!parentSnap.exists()) throw new Error("Parent folder not found");
    const parent = parentSnap.data() as FolderDoc;
    if (parent.parentId) {
      throw new Error("Folder hierarchy is limited to 2 levels");
    }
  }

  const payload = {
    userId: user.uid,
    name,
    color: input.color,
    icon: input.icon ?? null,
    parentId: input.parentId ?? null,
    order: input.order ?? Date.now(),
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };

  const created = await addDoc(foldersCol(user.uid), payload);

  return {
    id: created.id,
    ...payload,
    createdAt: null,
    updatedAt: null,
  } as FolderDoc;
}

// ─── Update ───────────────────────────────────────────────────────────────
export type UpdateFolderPatch = Partial<
  Pick<FolderDoc, "name" | "color" | "icon" | "parentId" | "order">
>;

export async function updateFolder(
  folderId: string,
  patch: UpdateFolderPatch,
): Promise<void> {
  const user = auth.currentUser;
  if (!user) throw new Error("Not signed in");

  // Hierarchy guards — only re-validate when parentId is actually changing.
  if (patch.parentId !== undefined) {
    if (patch.parentId === folderId) {
      throw new Error("A folder cannot be its own parent");
    }
    if (patch.parentId) {
      // New parent must exist and must itself be a root folder.
      const parentSnap = await getDoc(folderRef(user.uid, patch.parentId));
      if (!parentSnap.exists()) throw new Error("Parent folder not found");
      const parent = parentSnap.data() as FolderDoc;
      if (parent.parentId) {
        throw new Error("Folder hierarchy is limited to 2 levels");
      }
      // The folder being updated must not itself have children — otherwise
      // moving it under a parent would create a 3-level chain.
      const childrenSnap = await getDocs(
        query(foldersCol(user.uid), where("parentId", "==", folderId)),
      );
      if (!childrenSnap.empty) {
        throw new Error("Move children out before nesting this folder");
      }
    }
  }

  await updateDoc(folderRef(user.uid, folderId), {
    ...patch,
    updatedAt: serverTimestamp(),
  });
}

// ─── Delete ───────────────────────────────────────────────────────────────
// Cascade-safe delete:
//   1. Reject if the folder still has children (UX: user must relocate them).
//   2. Strip `folderId` from every fiche's `folderIds` array via batched
//      updates (one batch per 500 fiches — Firestore batch limit).
//   3. Delete the folder doc itself.
//
// Not transactional across steps — if step 3 fails, the folder stays but
// fiches are already cleaned. Safe failure mode (the folder just reappears
// empty), and the user can retry.
export async function deleteFolder(folderId: string): Promise<void> {
  const user = auth.currentUser;
  if (!user) throw new Error("Not signed in");

  const childrenSnap = await getDocs(
    query(foldersCol(user.uid), where("parentId", "==", folderId)),
  );
  if (!childrenSnap.empty) {
    throw new Error("Cannot delete a folder that still contains sub-folders");
  }

  // Strip folderId from every fiche that references it.
  const taggedSnap = await getDocs(
    query(fichesCol(user.uid), where("folderIds", "array-contains", folderId)),
  );
  const BATCH_SIZE = 400;
  for (let i = 0; i < taggedSnap.docs.length; i += BATCH_SIZE) {
    const batch = writeBatch(db);
    for (const fiche of taggedSnap.docs.slice(i, i + BATCH_SIZE)) {
      const current = (fiche.data().folderIds ?? []) as string[];
      batch.update(fiche.ref, {
        folderIds: current.filter((id) => id !== folderId),
        updatedAt: serverTimestamp(),
      });
    }
    await batch.commit();
  }

  await deleteDoc(folderRef(user.uid, folderId));
}

// ─── Read (live subscription) ────────────────────────────────────────────
export function subscribeMyFolders(
  cb: (folders: FolderDoc[]) => void,
): Unsubscribe {
  const user = auth.currentUser;
  if (!user) {
    cb([]);
    return () => {};
  }
  const q = query(foldersCol(user.uid), orderBy("order", "asc"));
  return onSnapshot(q, (snap) => {
    cb(
      snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<FolderDoc, "id">) })),
    );
  });
}

// ─── Tree shape helper ────────────────────────────────────────────────────
// Splits a flat folder list into {root, childrenByParent} for rendering.
// Consumers walk `roots`, then look up each root's children via the map.
export type FolderTree = {
  roots: FolderDoc[];
  childrenByParent: Map<string, FolderDoc[]>;
};

export function buildFolderTree(folders: FolderDoc[]): FolderTree {
  const roots: FolderDoc[] = [];
  const childrenByParent = new Map<string, FolderDoc[]>();
  for (const f of folders) {
    if (f.parentId) {
      const siblings = childrenByParent.get(f.parentId) ?? [];
      siblings.push(f);
      childrenByParent.set(f.parentId, siblings);
    } else {
      roots.push(f);
    }
  }
  // Sort each bucket by order asc.
  const byOrder = (a: FolderDoc, b: FolderDoc) => a.order - b.order;
  roots.sort(byOrder);
  for (const arr of childrenByParent.values()) arr.sort(byOrder);
  return { roots, childrenByParent };
}
